import type { OutreachOperation } from '@discovr/types';
import { MISSING_TEXT_SKIP_REASONS } from '../draftLabels';

/**
 * The one place an approve_drafts result is sorted into what the confirmation reports.
 * A skip reason lands in a bucket by the table below; one it does not know goes to `other`
 * with its reason, and an outcome status it does not know is counted in `unrecognised` —
 * neither is dropped or read as approved. A new Go result is one entry here plus its line in
 * the confirmation.
 *
 * An `applied` outcome is split by its reason: none means a message is on its way; `waiting_email` (G4) is a draft
 * approved for a creator with no address yet and has no message to send; any other reason goes to `other`.
 * Only the first is ever counted as sent.
 */
const WAITING_EMAIL_REASON = 'waiting_email';
const NOT_FROM_WIZARD_REASON = 'not_from_wizard';
const NO_EMAIL_REASONS: readonly string[] = ['no_contact'];
const SUPPRESSED_REASONS: readonly string[] = ['suppressed'];
const MISSING_TEXT_REASONS: readonly string[] = MISSING_TEXT_SKIP_REASONS;

export interface ReasonGroup {
  reason: string;
  ids: number[];
}

export interface ApproveSummary {
  /** Applied with a message on its way: sent now, or scheduled. The only count that may be called sent. */
  sentIds: number[];
  /** Applied with no address yet: nothing is sent until the creator's email arrives. */
  waitingEmailIds: number[];
  /** No address: approve's skips plus creators left out earlier in the wizard. */
  noEmail: number;
  suppressedIds: number[];
  /** suppressedIds plus creators left out earlier as do-not-contact. */
  suppressed: number;
  missingText: ReasonGroup[];
  /** Skip reasons this module has no bucket for. */
  other: ReasonGroup[];
  otherCount: number;
  /** Outcome statuses that are none of applied / skipped / failed. */
  unrecognised: number;
}

/**
 * Go answers 202 for a batch it will not honour (expired, unknown or another campaign's): the approve then ends done
 * with every first touch skipped as not_from_wizard. That is an expired batch, not a result to report.
 */
export function approveBatchExpired(operation: OutreachOperation): boolean {
  const outcomes = operation.outcomes ?? [];
  return outcomes.length > 0 && outcomes.every((o) => o.outcome === 'skipped' && o.reason === NOT_FROM_WIZARD_REASON);
}

export function summarizeApprove(
  operation: OutreachOperation,
  dropped: { noEmailDropped?: number; suppressedDropped?: number } = {},
): ApproveSummary {
  const sentIds: number[] = [];
  const waitingEmailIds: number[] = [];
  const byReason = new Map<string, number[]>();
  let unrecognised = 0;
  for (const o of operation.outcomes ?? []) {
    if (o.outcome === 'applied' && !o.reason) sentIds.push(Number(o.itemKey));
    else if (o.outcome === 'applied' && o.reason === WAITING_EMAIL_REASON) waitingEmailIds.push(Number(o.itemKey));
    else if (o.outcome === 'applied' || o.outcome === 'skipped') {
      // An applied reason this page does not know is never read as sent: it is reported with the other results.
      const reason = o.reason ?? 'unknown';
      byReason.set(reason, [...(byReason.get(reason) ?? []), Number(o.itemKey)]);
    } else if (o.outcome !== 'failed') unrecognised += 1;
  }
  const idsFor = (reasons: readonly string[]) => reasons.flatMap((reason) => byReason.get(reason) ?? []);
  const groups = (reasons: readonly string[]): ReasonGroup[] => reasons.flatMap((reason) => (byReason.has(reason) ? [{ reason, ids: byReason.get(reason) ?? [] }] : []));
  const known = new Set([...NO_EMAIL_REASONS, ...SUPPRESSED_REASONS, ...MISSING_TEXT_REASONS]);
  const other = [...byReason].filter(([reason]) => !known.has(reason)).map(([reason, ids]) => ({ reason, ids }));
  const suppressedIds = idsFor(SUPPRESSED_REASONS);
  return {
    sentIds,
    waitingEmailIds,
    noEmail: (dropped.noEmailDropped ?? 0) + idsFor(NO_EMAIL_REASONS).length,
    suppressedIds,
    suppressed: suppressedIds.length + (dropped.suppressedDropped ?? 0),
    missingText: groups(MISSING_TEXT_REASONS),
    other,
    otherCount: other.reduce((n, g) => n + g.ids.length, 0),
    unrecognised,
  };
}
